export const BATCHES_PERMISSIONS = ["certificate.read"] as const;
export const CERTIFICATES_PERMISSIONS = ["certificate.read"] as const;
export const MEMBERS_PERMISSIONS = ["member.read"] as const;
export const PAYMENTS_PERMISSIONS = ["payment.read"] as const;
export const TEMPLATES_PERMISSIONS = ["template.read"] as const;
export const SIGNATURES_PERMISSIONS = ["signature.read"] as const;
// Uploading needs manage, but listing fonts and images and fetching their files needs read.
export const FONTS_PERMISSIONS = ["template.manage", "template.read"] as const;
// Editing a layout needs manage, and loading the version, fonts and images needs read.
export const DESIGNER_PERMISSIONS = ["template.manage", "template.read"] as const;
// The approval queue loads batch rows, so it needs read access as well as approve.
export const APPROVALS_PERMISSIONS = ["batch.approve", "certificate.read"] as const;
export const DELIVERIES_PERMISSIONS = ["certificate.read"] as const;
export const EMAIL_TEMPLATES_PERMISSIONS = ["certificate.send"] as const;
export const PRINTERS_PERMISSIONS = ["certificate.print"] as const;
export const PROGRAMS_PERMISSIONS = ["program.read"] as const;
export const USERS_PERMISSIONS = ["user.read"] as const;
export const ROLES_PERMISSIONS = ["role.read"] as const;
export const AUDIT_PERMISSIONS = ["audit.read"] as const;
export const ANALYTICS_PERMISSIONS = ["analytics.read"] as const;
export const REVOCATIONS_PERMISSIONS = ["certificate.revoke.approve"] as const;
export const REISSUES_PERMISSIONS = ["certificate.reissue.approve"] as const;

export type NavItem = {
  to:
    | "/batches"
    | "/certificates"
    | "/deliveries"
    | "/members"
    | "/payments"
    | "/templates"
    | "/email-templates"
    | "/signatures"
    | "/fonts"
    | "/printers"
    | "/programs"
    | "/users"
    | "/roles"
    | "/audit"
    | "/analytics"
    | "/approvals"
    | "/approvals/revocations"
    | "/approvals/reissues";
  label: string;
  // Set when the visible label repeats another item's, e.g. the two "Batches" links.
  ariaLabel?: string;
  permissions: readonly string[];
  // "/approvals" would otherwise also light up on "/approvals/revocations".
  exact?: boolean;
};

export type NavGroup = { label: string; items: readonly NavItem[] };

// Approvals lead: they are time-sensitive and approvers land there.
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: "Approvals",
    items: [
      { to: "/approvals", label: "Batches", ariaLabel: "Batches awaiting approval", permissions: APPROVALS_PERMISSIONS, exact: true },
      { to: "/approvals/revocations", label: "Revocations", permissions: REVOCATIONS_PERMISSIONS },
      { to: "/approvals/reissues", label: "Re-issues", permissions: REISSUES_PERMISSIONS },
    ],
  },
  {
    label: "Certificates",
    items: [
      { to: "/batches", label: "Batches", permissions: BATCHES_PERMISSIONS },
      { to: "/certificates", label: "Certificates", permissions: CERTIFICATES_PERMISSIONS },
      { to: "/members", label: "Members", permissions: MEMBERS_PERMISSIONS },
      { to: "/payments", label: "Payments", permissions: PAYMENTS_PERMISSIONS },
    ],
  },
  {
    label: "Distribution",
    items: [
      { to: "/deliveries", label: "Deliveries", permissions: DELIVERIES_PERMISSIONS },
      { to: "/email-templates", label: "Email templates", permissions: EMAIL_TEMPLATES_PERMISSIONS },
      { to: "/printers", label: "Printers", permissions: PRINTERS_PERMISSIONS },
    ],
  },
  {
    label: "Design",
    items: [
      { to: "/programs", label: "Programs", permissions: PROGRAMS_PERMISSIONS },
      { to: "/templates", label: "Templates", permissions: TEMPLATES_PERMISSIONS },
      { to: "/signatures", label: "Signatures", permissions: SIGNATURES_PERMISSIONS },
      { to: "/fonts", label: "Fonts", permissions: FONTS_PERMISSIONS },
    ],
  },
  {
    label: "Reports",
    items: [
      { to: "/analytics", label: "Analytics", permissions: ANALYTICS_PERMISSIONS },
      { to: "/audit", label: "Audit", permissions: AUDIT_PERMISSIONS },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/users", label: "Users", permissions: USERS_PERMISSIONS },
      { to: "/roles", label: "Roles", permissions: ROLES_PERMISSIONS },
    ],
  },
];

export const NAV: readonly NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

export function visibleNavGroups(can: (permission: string) => boolean): NavGroup[] {
  return NAV_GROUPS.map((group) => ({ ...group, items: group.items.filter((item) => item.permissions.every(can)) })).filter(
    (group) => group.items.length > 0,
  );
}

export function visibleNav(can: (permission: string) => boolean): NavItem[] {
  return NAV.filter((item) => item.permissions.every(can));
}

/** Where "/" sends a user: approvers land on their queue, everyone else on the first screen they can open. */
export function landingPath(can: (permission: string) => boolean): NavItem["to"] | null {
  const visible = visibleNav(can);
  return (visible.find((item) => item.to === "/approvals") ?? visible[0])?.to ?? null;
}
